import { useEffect } from "react";
import { setPageMeta } from "../lib/pageMeta";

// Terms of Service — full legal document (Round 7). The "Last updated"
// date comes from the document itself, never the clock. Anchored sections
// with a table of contents; the page shell (header/footer/styling) is the
// shared legal-page chrome.

const LAST_UPDATED = "June 10, 2026";

const SECTIONS: Array<[string, string]> = [
  ["what-casethread-is", "1. What CaseThread is — and is not"],
  ["ai-accuracy", "2. AI-assisted features and accuracy"],
  ["your-account", "3. Your account"],
  ["your-content", "4. Your content"],
  ["acceptable-use", "5. Acceptable use"],
  ["billing", "6. Plans, trials, and billing"],
  ["byok", "7. Bring-your-own-key (BYOK)"],
  ["email-intake", "8. Email intake addresses"],
  ["availability", "9. Service availability and changes"],
  ["termination", "10. Termination"],
  ["warranties", "11. Disclaimer of warranties"],
  ["liability", "12. Limitation of liability"],
  ["indemnification", "13. Indemnification"],
  ["governing-law", "14. Governing law and disputes"],
  ["changes", "15. Changes to these terms"],
  ["misc", "16. Miscellaneous"],
  ["contact", "17. Contact"],
];

export default function TermsPage() {
  useEffect(() => {
    setPageMeta({
      title: "Terms of service | CaseThread",
      description:
        "Terms governing use of CaseThread — what the service is and is not, how AI-assisted features work, billing, and your responsibilities.",
      canonical: "https://casethread.app/terms",
    });
  }, []);

  return (
    <div className="legal-page">
      <header className="legal-header">
        <a href="/welcome" className="legal-brand">
          CaseThread
        </a>
        <nav>
          <a href="/privacy">Privacy</a>
        </nav>
      </header>
      <main className="legal-main">
        <h1>Terms of Service</h1>
        <p className="muted">
          <strong>Last updated: {LAST_UPDATED}</strong>
        </p>

        <p>
          CaseThread is a personal correspondence management tool. It organizes
          emails, files, screenshots, recordings, and notes into a single
          dossier. CaseThread is not a law firm and does not provide legal
          advice. These terms govern your use of the service.
        </p>
        <p>
          Please read these terms carefully. By creating an account or using
          CaseThread, you agree to them. If you do not agree, do not use the
          service.
        </p>

        <nav className="legal-toc" aria-label="Table of contents">
          <ul>
            {SECTIONS.map(([id, title]) => (
              <li key={id}>
                <a href={`#${id}`}>{title}</a>
              </li>
            ))}
          </ul>
        </nav>

        <h2 id="what-casethread-is">1. What CaseThread is — and is not</h2>
        <p>
          CaseThread (the "Service"), available at casethread.app, helps you
          organize and review your own dispute-related correspondence and
          documents. It extracts facts, dates, amounts, and quotes from
          material you provide, and presents them in timelines, summaries, and
          exports.
        </p>
        <p>
          <strong>
            CaseThread is not a law firm, does not provide legal advice, and
            using it does not create an attorney–client relationship.
          </strong>{" "}
          Nothing the Service produces — summaries, timelines, extracted
          facts, exported documents — is legal advice or a substitute for
          consulting a qualified attorney about your situation. Decisions
          about your dispute, claim, or legal matter are yours alone.
        </p>

        <h2 id="ai-accuracy">2. AI-assisted features and accuracy</h2>
        <p>
          CaseThread uses artificial intelligence to help process your
          documents. You should understand how to interpret its output:
        </p>
        <ul>
          <li>
            <strong>Verified content.</strong> Quoted text and dollar amounts
            marked as verified have been mechanically checked against your
            source documents — each must match the source exactly before it
            is shown.
          </li>
          <li>
            <strong>AI assistance.</strong> Titles, summaries, party
            attributions, category groupings, and similar elements are
            AI-generated assistance to help you scan your records. They may
            contain errors. When anything matters, the verbatim quote and your
            original document are the authority.
          </li>
          <li>
            <strong>Your responsibility.</strong> You are responsible for
            verifying any information before relying on it or sending it to a
            third party (such as an insurance company, opposing party, court,
            or regulator). Always review output against your original
            documents.
          </li>
        </ul>
        <p>
          We do not guarantee that AI-generated content is accurate, complete,
          or suitable for any particular purpose.
        </p>

        <h2 id="your-account">3. Your account</h2>
        <p>
          You must be at least 18 years old to use CaseThread. You are
          responsible for maintaining the security of your account credentials
          and for all activity under your account. Authentication is provided
          through our identity provider (Clerk). Notify us promptly if you
          believe your account has been compromised.
        </p>

        <h2 id="your-content">4. Your content</h2>
        <p>
          "Your content" means everything you forward, upload, paste, record,
          or enter into the Service — emails, attachments, documents, images,
          audio, and notes.
        </p>
        <ul>
          <li>
            <strong>You own your content.</strong> CaseThread claims no
            ownership of it.
          </li>
          <li>
            <strong>License to operate.</strong> You grant us a limited
            license to store, process, display, and transmit your content
            solely to provide the Service to you (including processing by the
            AI providers described in our Privacy Policy). This license ends
            when you delete the content or your account, except as needed for
            backups to expire or to comply with law.
          </li>
          <li>
            <strong>Your responsibility for content.</strong> You represent
            that you have the right to submit the content you provide,
            including emails you forward and recordings you upload. You are
            responsible for complying with laws that apply to your content —
            for example, laws governing the recording of phone calls, which
            vary by state and may require the consent of all parties.
          </li>
          <li>
            <strong>Originals stay with you.</strong> CaseThread is an
            organizational tool, not a system of record. Keep your original
            documents. Do not treat the Service as your only copy of anything
            important, including material you may need as evidence.
          </li>
        </ul>

        <h2 id="acceptable-use">5. Acceptable use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>violate any law or the rights of others;</li>
          <li>
            store or distribute content you do not have the right to use;
          </li>
          <li>
            upload malicious code or attempt to probe, breach, or disrupt the
            Service or its infrastructure;
          </li>
          <li>attempt to access another user's data;</li>
          <li>
            resell, sublicense, or provide the Service to third parties as
            your own offering;
          </li>
          <li>
            use automated means to access the Service except through
            interfaces we provide.
          </li>
        </ul>
        <p>We may suspend or terminate accounts that violate these terms.</p>

        <h2 id="billing">6. Plans, trials, and billing</h2>
        <ul>
          <li>
            <strong>Free trial.</strong> New accounts receive a 30-day free
            trial. No charge is made until the trial ends.
          </li>
          <li>
            <strong>Subscription.</strong> After the trial, continued use
            requires a paid subscription, currently{" "}
            <strong>$9.99/month or $89.99/year</strong> (prices may change
            with notice as described below). Payment is processed by Stripe;
            we do not store your card details.
          </li>
          <li>
            <strong>Renewal and cancellation.</strong> Subscriptions renew
            automatically until cancelled. You can cancel at any time from
            your account settings; cancellation takes effect at the end of
            the current billing period, and you retain access until then.
          </li>
          <li>
            <strong>Refunds.</strong> Except where required by law, payments
            are non-refundable. If something has gone wrong, contact us — we
            would rather fix it than argue about it.
          </li>
          <li>
            <strong>Usage limits.</strong> Plans include usage limits on AI
            features (such as a monthly cap on AI processing calls). We may
            offer options to continue beyond the cap, including
            bring-your-own-key (BYOK) configurations.
          </li>
          <li>
            <strong>Price changes.</strong> We may change prices with at
            least 30 days' notice. Changes apply at your next renewal.
          </li>
        </ul>

        <h2 id="byok">7. Bring-your-own-key (BYOK)</h2>
        <p>
          If you configure your own API keys for third-party AI providers,
          those keys are used to process your content directly with that
          provider under <strong>your</strong> agreement with them. You are
          responsible for the security of your keys, for charges the provider
          bills you, and for complying with the provider's terms.
        </p>

        <h2 id="email-intake">8. Email intake addresses</h2>
        <p>
          The Service may provide you with unique email addresses (such as
          per-case addresses at casethread.app) for forwarding correspondence.
          Mail sent to these addresses becomes part of your content. Keep
          these addresses reasonably private; you can regenerate or disable
          them. We may filter, rate-limit, or discard mail that appears to be
          spam, bulk, or abusive.
        </p>

        <h2 id="availability">9. Service availability and changes</h2>
        <p>
          We work to keep the Service reliable, but it is provided without
          uptime guarantees. We may add, change, or remove features, and may
          suspend the Service for maintenance. If we ever discontinue the
          Service, we will make reasonable efforts to give you at least 30
          days' notice and the ability to export your data.
        </p>

        <h2 id="termination">10. Termination</h2>
        <p>
          You may close your account at any time. We may suspend or terminate
          your account for violation of these terms, for non-payment, or
          where required by law. On termination, your right to use the
          Service ends; deletion of your data is handled as described in the
          Privacy Policy. Sections of these terms that by their nature should
          survive termination (including Sections 11–14) do survive.
        </p>

        <h2 id="warranties">11. Disclaimer of warranties</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT
          WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND
          NON-INFRINGEMENT. WITHOUT LIMITING THE FOREGOING, WE DO NOT WARRANT
          THAT THE SERVICE WILL BE ERROR-FREE OR UNINTERRUPTED, THAT
          AI-GENERATED OUTPUT WILL BE ACCURATE OR COMPLETE, OR THAT THE
          SERVICE IS SUITABLE FOR USE AS EVIDENCE OR IN ANY LEGAL PROCEEDING.
        </p>

        <h2 id="liability">12. Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW: (A) WE WILL NOT BE LIABLE
          FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
          DAMAGES, OR FOR LOST PROFITS, LOST DATA, OR THE OUTCOME OF ANY
          DISPUTE, CLAIM, NEGOTIATION, OR LEGAL MATTER IN WHICH YOU USE THE
          SERVICE; AND (B) OUR TOTAL LIABILITY FOR ALL CLAIMS RELATING TO THE
          SERVICE WILL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12)
          MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM (OR $100 IF YOU
          HAVE PAID NOTHING).
        </p>
        <p>
          Some jurisdictions do not allow certain limitations, so parts of
          this section may not apply to you. In those jurisdictions, our
          liability is limited to the smallest amount permitted by law.
        </p>

        <h2 id="indemnification">13. Indemnification</h2>
        <p>
          You agree to indemnify and hold us harmless from claims, damages,
          and expenses (including reasonable attorneys' fees) arising from
          your content, your use of the Service in violation of these terms,
          or your violation of law or the rights of a third party.
        </p>

        <h2 id="governing-law">14. Governing law and disputes</h2>
        <p>
          These terms are governed by the laws of the State of Illinois,
          without regard to conflict-of-law rules. Disputes will be resolved
          in the state or federal courts located in Cook County, Illinois,
          and you consent to their jurisdiction — except that either party
          may bring an individual claim in small-claims court. To the extent
          permitted by law, each party waives any right to a jury trial and
          agrees that disputes will be brought individually, not as part of a
          class action.
        </p>

        <h2 id="changes">15. Changes to these terms</h2>
        <p>
          We may update these terms from time to time. For material changes,
          we will provide notice (for example, by email or an in-app notice)
          at least 14 days before the changes take effect. Continued use of
          the Service after changes take effect constitutes acceptance. The
          "Last updated" date above reflects the current version.
        </p>

        <h2 id="misc">16. Miscellaneous</h2>
        <p>
          These terms (together with the Privacy Policy) are the entire
          agreement between you and CaseThread regarding the Service. If any
          provision is found unenforceable, the rest remains in effect. Our
          failure to enforce a provision is not a waiver. You may not assign
          these terms without our consent; we may assign them in connection
          with a sale or reorganization of the Service.
        </p>

        <h2 id="contact">17. Contact</h2>
        <p>
          Questions about these terms: use the Feedback link in the app or
          the contact information on casethread.app.
        </p>
      </main>
      <footer className="legal-footer">
        <p>
          <a href="/guides">Read the dispute resolution guides</a> ·{" "}
          <a href="/welcome">About CaseThread</a>
        </p>
        <p className="muted">© 2026 CaseThread</p>
      </footer>
    </div>
  );
}
